import { Page } from '@/shared/pagination/export';
import { MarketSignalStream } from '@/modules/market-signals/model/market-signals/market-signal-stream';
import axios from 'axios';
import { MarketSignal } from '@/modules/market-signals/model/market-signals/market-signal';
import { MarketSignalInfoSource } from '@/modules/market-signals/model/market-signals/market-signal-info-source';
import {
  fixMarketSignalStreamAnalysis,
  MarketSignalStreamAnalysis,
} from '@/modules/market-signals/model/market-signals/market-signal-stream-analysis';
import { MarketSignalAssistantResponse } from '@/modules/market-signals/model/market-signals/market-signal-assistant-response';
import { MarketSignalAlarmConfig } from '@/modules/market-signals/model/market-signals/market-signal-alarm-config';
import { MarketSignalAlarm } from '@/modules/market-signals/model/market-signals/market-signal-alarm';
import { MarketSignalPopulatedAlarm } from '@/modules/market-signals/model/market-signals/market-signal-populated-alarm';

async function getStreams(): Promise<Page<MarketSignalStream>> {
  return (await axios.get('/api/signal-analysis/streams')).data;
}

async function getSignalsByStream(stream: string, page: number): Promise<Page<MarketSignal>> {
  return (await axios.get(`/api/signal-analysis/streams/${stream}/signals?page=${page}`)).data;
}

async function getSignalsByIds(ids: string[]): Promise<Page<MarketSignal>> {
  const q = ids.map(value => `id=${encodeURIComponent(value)}`).join('&');

  return (await axios.get(`/api/signal-analysis/signals?${q}&size=${ids.length}`)).data;
}

async function getSignalsByInfoSource(scheme: string, code: string): Promise<Page<MarketSignal>> {
  return (
    await axios.get(
      `/api/signal-analysis/signals/by-source/${encodeURIComponent(scheme)}/${encodeURIComponent(code)}`,
    )
  ).data;
}

async function getInfoSources(page = 0): Promise<Page<MarketSignalInfoSource>> {
  return (await axios.get(`/api/signal-analysis/info-sources?page=${page}`)).data;
}

async function saveStream(stream: MarketSignalStream): Promise<MarketSignalStream> {
  return (await axios.put(`/api/signal-analysis/streams`, stream)).data;
}

async function getAnalysisByStream(
  steam: string,
  page: number,
): Promise<Page<MarketSignalStreamAnalysis>> {
  const result = (await axios.get(`/api/signal-analysis/sentiment-analysis/${steam}?page=${page}`))
    .data as Page<MarketSignalStreamAnalysis>;

  result.content = result.content.map(fixMarketSignalStreamAnalysis);

  return result;
}

async function queryAssistant(steam: string, q: string): Promise<MarketSignalAssistantResponse> {
  return (
    await axios.post(`/api/signal-analysis/assistant?stream=${steam}&q=${encodeURIComponent(q)}`)
  ).data;
}

async function getAlarmConfigs(page = 0): Promise<Page<MarketSignalAlarmConfig>> {
  return (await axios.get(`/api/signal-analysis/alert-condition?page=${page}&size=1000`)).data;
}

async function toggleAlarmEnable(id: string, enabled: boolean): Promise<MarketSignalAlarmConfig> {
  return (
    await axios.patch(`/api/signal-analysis/alert-condition/${id}`, {
      enabled,
    })
  ).data;
}

async function saveAlarmConfig(
  id: string,
  config: MarketSignalAlarmConfig,
): Promise<MarketSignalAlarmConfig> {
  const { _id, ...rest } = config;
  return (await axios.put(`/api/signal-analysis/alert-condition/${id}`, rest)).data;
}

async function createAlarmConfig(
  config: MarketSignalAlarmConfig,
): Promise<MarketSignalAlarmConfig> {
  return (await axios.post(`/api/signal-analysis/alert-condition`, config)).data;
}

async function getAlarms(page = 0, size = 10): Promise<Page<MarketSignalAlarm>> {
  return (await axios.get(`/api/signal-analysis/alert?page=${page}&size=${size}`)).data;
}

async function acknowledgeAlarm(id: string): Promise<MarketSignalAlarm | null> {
  return (await axios.patch(`/api/signal-analysis/alert/${id}`)).data;
}

async function getSignalsByAlarmCondition(
  id: string,
  page = 0,
  size = 10,
): Promise<Page<MarketSignalPopulatedAlarm>> {
  return (
    await axios.get(
      `/api/signal-analysis/alert/signals-by-condition/${id}?page=${page}&size=${size}`,
    )
  ).data;
}

const api = {
  getStreams,
  getSignalsByStream,
  getInfoSources,
  saveStream,
  getAnalysisByStream,
  queryAssistant,
  getSignalsByIds,
  getAlarmConfigs,
  toggleAlarmEnable,
  saveAlarmConfig,
  createAlarmConfig,
  getAlarms,
  acknowledgeAlarm,
  getSignalsByAlarmCondition,
  getSignalsByInfoSource,
};

export default api;
